import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Chip,
  Avatar,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  ArrowForward,
  ArrowBack as ArrowBackIcon,
  CalendarToday,
  LocationOn,
  Gavel,
  AccessTime,
} from '@mui/icons-material';
import {
  useTournament,
  useCreateTournamentMatch,
  useTeams,
  usePlayers,
  useVenues,
  useOfficials,
} from '@/hooks/useApi';
import { LoadingState } from '@/components';
import type { SportType } from '@/types';

const steps = ['Выбор участников', 'Время и место', 'Судьи', 'Подтверждение'];

const stages = [
  { value: 'regular', label: 'Обычный' },
  { value: 'group', label: 'Групповой этап' },
  { value: 'playoff', label: 'Плей-офф' },
  { value: 'swiss', label: 'Швейцарка' },
  { value: 'final', label: 'Финал' },
];

export const CreateMatchPage: React.FC = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [participantType, setParticipantType] = useState<'team' | 'player'>('team');
  const [formData, setFormData] = useState({
    round: 1,
    stage: 'regular' as 'group' | 'playoff' | 'regular' | 'swiss' | 'final',
    scheduledAt: '',
    homeId: '',
    awayId: '',
    venueId: '',
    officialsIds: [] as string[],
  });

  const { data: tournament, isLoading: tournamentLoading } = useTournament(tournamentId || '');
  const { data: teams } = useTeams({ sportType: tournament?.sportType, limit: 100 });
  const { data: players } = usePlayers({ limit: 100 });
  const { data: venues } = useVenues();
  const { data: officials } = useOfficials();
  const createMatch = useCreateTournamentMatch();

  // Определяем тип участников на основе вида спорта
  useEffect(() => {
    if (tournament?.sportType) {
      const individualSports: SportType[] = ['tennis', 'poker', 'swimming'];
      setParticipantType(individualSports.includes(tournament.sportType) ? 'player' : 'team');
    }
  }, [tournament?.sportType]);

  const participants = participantType === 'team' ? teams?.data : players?.data;

  const handleNext = () => {
    // Validation
    if (activeStep === 0) {
      if (!formData.homeId || !formData.awayId) {
        setError('Выберите обоих участников');
        return;
      }
      if (formData.homeId === formData.awayId) {
        setError('Участники должны быть разными');
        return;
      }
    }
    if (activeStep === 1 && !formData.scheduledAt) {
      setError('Укажите дату и время матча');
      return;
    }
    
    setError('');
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      await createMatch.mutateAsync({
        tournamentId: tournamentId!,
        data: {
          ...formData,
          scheduledAt: formData.scheduledAt ? new Date(formData.scheduledAt).toISOString() : undefined,
        },
      });
      navigate(`/tournaments/${tournamentId}`);
    } catch (err) {
      setError('Ошибка при создании матча');
    }
  };

  const getParticipantName = (id: string) => {
    const p = participants?.find(p => p.id === id);
    if (!p) return '—';
    return 'firstName' in p ? `${p.firstName} ${p.lastName || ''}` : p.name;
  };

  const getParticipantAvatar = (id: string) => {
    const p = participants?.find(p => p.id === id);
    if (!p) return '?';
    return 'firstName' in p 
      ? `${p.firstName.charAt(0)}${p.lastName?.charAt(0) || ''}`
      : p.name.charAt(0);
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Раунд / Тур"
                  type="number"
                  value={formData.round}
                  onChange={(e) => setFormData({ ...formData, round: parseInt(e.target.value) })}
                  InputProps={{
                    startAdornment: <AccessTime color="action" sx={{ mr: 1 }} />,
                  }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Стадия</InputLabel>
                  <Select
                    value={formData.stage}
                    onChange={(e) => setFormData({ ...formData, stage: e.target.value as typeof formData.stage })}
                  >
                    {stages.map((s) => (
                      <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider />

            <Typography variant="subtitle2" color="text.secondary">
              Выберите {participantType === 'team' ? 'команды' : 'игроков'}:
            </Typography>

            <FormControl fullWidth>
              <InputLabel>{participantType === 'team' ? 'Хозяева' : 'Игрок 1'}</InputLabel>
              <Select
                value={formData.homeId}
                onChange={(e) => setFormData({ ...formData, homeId: e.target.value })}
                renderValue={(selected) => getParticipantName(selected as string)}
              >
                {participants?.map((p) => (
                  <MenuItem key={p.id} value={p.id} disabled={p.id === formData.awayId}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                        {'firstName' in p ? p.firstName.charAt(0) : p.name.charAt(0)}
                      </Avatar>
                      <Typography>
                        {'firstName' in p ? `${p.firstName} ${p.lastName || ''}` : p.name}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="text.secondary">VS</Typography>
            </Box>

            <FormControl fullWidth>
              <InputLabel>{participantType === 'team' ? 'Гости' : 'Игрок 2'}</InputLabel>
              <Select
                value={formData.awayId}
                onChange={(e) => setFormData({ ...formData, awayId: e.target.value })}
                renderValue={(selected) => getParticipantName(selected as string)}
              >
                {participants?.map((p) => (
                  <MenuItem key={p.id} value={p.id} disabled={p.id === formData.homeId}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                        {'firstName' in p ? p.firstName.charAt(0) : p.name.charAt(0)}
                      </Avatar>
                      <Typography>
                        {'firstName' in p ? `${p.firstName} ${p.lastName || ''}` : p.name}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Дата и время матча"
              type="datetime-local"
              value={formData.scheduledAt}
              onChange={(e) => setFormData({ ...formData, scheduledAt: e.target.value })}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: <CalendarToday color="action" sx={{ mr: 1 }} />,
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Арена / Место проведения</InputLabel>
              <Select
                value={formData.venueId}
                onChange={(e) => setFormData({ ...formData, venueId: e.target.value })}
              >
                {venues?.data.map((venue) => (
                  <MenuItem key={venue.id} value={venue.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOn fontSize="small" />
                      <Box>
                        <Typography variant="body2">{venue.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{venue.address}</Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Alert severity="info">
              Если арена не выбрана, матч будет считаться выездным для обеих сторон.
            </Alert>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Выберите судей для матча (опционально):
            </Typography>

            <List>
              {officials?.data.map((official) => (
                <Paper key={official.id} sx={{ mb: 1 }}>
                  <ListItemButton
                    onClick={() => {
                      const isSelected = formData.officialsIds.includes(official.id);
                      setFormData({
                        ...formData,
                        officialsIds: isSelected
                          ? formData.officialsIds.filter(id => id !== official.id)
                          : [...formData.officialsIds, official.id],
                      });
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 48 }}>
                      <Avatar sx={{ bgcolor: formData.officialsIds.includes(official.id) ? 'primary.main' : 'grey.400' }}>
                        <Gavel />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={official.name}
                      secondary={official.roles?.join(', ')}
                    />
                    {formData.officialsIds.includes(official.id) && (
                      <Chip label="Выбран" size="small" color="primary" />
                    )}
                  </ListItemButton>
                </Paper>
              ))}
            </List>

            {formData.officialsIds.length > 0 && (
              <Alert severity="success">
                Выбрано судей: {formData.officialsIds.length}
              </Alert>
            )}
          </Box>
        );

      case 3:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Paper sx={{ p: 3, bgcolor: 'background.default' }}>
              <Typography variant="h6" gutterBottom align="center">
                {tournament?.name}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, my: 3 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 1, fontSize: 24 }}>
                    {getParticipantAvatar(formData.homeId)}
                  </Avatar>
                  <Typography fontWeight="bold">{getParticipantName(formData.homeId)}</Typography>
                </Box>
                
                <Typography variant="h4" color="text.secondary">VS</Typography>
                
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar sx={{ width: 64, height: 64, mx: 'auto', mb: 1, fontSize: 24 }}>
                    {getParticipantAvatar(formData.awayId)}
                  </Avatar>
                  <Typography fontWeight="bold">{getParticipantName(formData.awayId)}</Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <CalendarToday fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {formData.scheduledAt 
                    ? new Date(formData.scheduledAt).toLocaleString('ru-RU')
                    : 'Время не указано'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <LocationOn fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  {venues?.data.find(v => v.id === formData.venueId)?.name || 'Место не указано'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <AccessTime fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Раунд {formData.round} • {stages.find(s => s.value === formData.stage)?.label}
                </Typography>
                {formData.officialsIds.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    <Gavel fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Судей: {formData.officialsIds.length}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  if (tournamentLoading) return <LoadingState message="Загрузка..." />;

  return (
    <Box sx={{ pb: 10 }}>
      <Paper sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(`/tournaments/${tournamentId}`)}>
          Назад
        </Button>
        <Typography variant="h6" fontWeight="bold">
          Новый матч
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mx: 2 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Турнир: <strong>{tournament?.name}</strong>
        </Alert>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
          >
            Назад
          </Button>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSubmit}
              disabled={createMatch.isPending}
            >
              {createMatch.isPending ? 'Создание...' : 'Создать матч'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForward />}
            >
              Далее
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};
